import { Link } from 'react-router-dom';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';
import { Phone, Mail, MapPin } from 'lucide-react';
import { generalContact, decodeContactInfo } from '@/data/contacts';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const logoBase = '/images/logo';
  const logoSrc = buildSrc(logoBase, 256);
  const logoSrcSet = buildSrcSet(logoBase, IMAGE_WIDTHS.logo);

  return (
    <footer className="bg-card border-t border-border/50">
      {/* Main Footer Content */}
      <div className="py-20 border-b border-border/50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand & Contact */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={logoSrc} 
                  srcSet={logoSrcSet}
                  sizes="40px"
                  alt="Enno & Jens – Digital-Agentur Wernigerode" 
                  loading="lazy"
                  decoding="async"
                  width={40}
                  height={40}
                  className="w-10 h-10 rounded-lg object-contain"
                />
                <span className="font-display font-semibold text-lg text-foreground">Enno & Jens</span>
              </div>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                Ihre Digital-Agentur im Harz. Wir realisieren Webseiten, die Unternehmen wachsen lassen.
              </p>
              <div className="space-y-2">
                <a href={`tel:${decodeContactInfo(generalContact.phone)}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                  <Phone size={16} className="flex-shrink-0" />
                  {decodeContactInfo(generalContact.phone).replace(/^\+49/, '0')}
                </a>
                <a href={`mailto:${decodeContactInfo(generalContact.email)}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                  <Mail size={16} className="flex-shrink-0" />
                  {decodeContactInfo(generalContact.email)}
                </a>
                <div className="flex items-start gap-2 text-muted-foreground text-sm pt-2">
                  <MapPin size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    Goldhammer & Kerwel GbR<br />
                    Am Jägerkopf 4<br />
                    38855 Wernigerode
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-display font-semibold text-foreground mb-5">Leistungen</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/leistungen/webseitenerstellung" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Webseitenerstellung
                  </Link>
                </li>
                <li>
                  <Link to="/leistungen/onlineshop-erstellung" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    E-Commerce-Shops
                  </Link>
                </li>
                <li>
                  <Link to="/leistungen/seo-optimierung" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    SEO-Optimierung
                  </Link>
                </li>
                <li>
                  <Link to="/leistungen/hosting-domains" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Hosting & Domains
                  </Link>
                </li>
                <li>
                  <Link to="/leistungen" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium pt-2">
                    Alle Leistungen →
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-display font-semibold text-foreground mb-5">Über uns</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/ueber-uns" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Über uns
                  </Link>
                </li>
                <li>
                  <Link to="/so-arbeiten-wir" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    So arbeiten wir
                  </Link>
                </li>
                <li>
                  <Link to="/referenzen" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Referenzen
                  </Link>
                </li>
                <li>
                  <Link to="/kontakt" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium pt-2">
                    Kontakt →
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="font-display font-semibold text-foreground mb-5">Ressourcen</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/ratgeber" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Ratgeber
                  </Link>
                </li>
                <li>
                  <Link to="/branchen" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    Branchen
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              © {currentYear} Enno & Jens · Alle Rechte vorbehalten
            </p>
            <nav className="flex items-center gap-8" aria-label="Footer Links">
              <Link to="/impressum" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                Datenschutz
              </Link>
              <Link to="/agb" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                AGB
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
